import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components';
import { AmbossEmbed } from './AmbossEmbed';
import { AmbossHelpOverlay } from './AmbossHelpOverlay';
import { AmbossHistoryView } from './AmbossHistoryView';
import { AmbossSearchPanel } from './AmbossSearchPanel';
import { AmbossEmbedFrame, AmbossHistoryOverlay, AmbossOverlay } from './amboss.styles';
import {
  getAmbossDataGatePhase,
  retryAmbossDataGate,
  subscribeToAmbossDataGate,
} from './ambossDataGate';
import { useAmboss } from './useAmboss';

export function AmbossLayer({ topOffsetPx }: { topOffsetPx: number }) {
  const { t } = useTranslation();
  const { screen, overlay, embedUrl, submissionId, reportCid } = useAmboss();
  const dataGatePhase = useSyncExternalStore(subscribeToAmbossDataGate, getAmbossDataGatePhase);

  if (dataGatePhase !== 'ready') {
    if (dataGatePhase === 'blocked' && screen !== 'closed') {
      return (
        <AmbossOverlay style={{ top: topOffsetPx }}>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="text-sm text-foreground">{t('amboss.embed.error')}</p>
            <Button onClick={retryAmbossDataGate}>{t('amboss.embed.retry')}</Button>
          </div>
        </AmbossOverlay>
      );
    }
    return null;
  }

  return (
    <>
      {embedUrl && (
        // Kept mounted while not the active screen and moved offscreen
        // instead of CSS-hidden: hiding an Electron <webview> can leak or
        // reload its guest. The inert frame ignores input while offscreen.
        <AmbossEmbedFrame
          style={{
            top: topOffsetPx,
            transform: screen !== 'embedded' ? 'translateY(200vh)' : undefined,
          }}
          inert={screen !== 'embedded'}
        >
          <AmbossEmbed url={embedUrl} reloadToken={submissionId} onCid={reportCid} />
        </AmbossEmbedFrame>
      )}
      {screen === 'search' && overlay === 'none' && (
        <AmbossOverlay style={{ top: topOffsetPx }}>
          <AmbossSearchPanel />
        </AmbossOverlay>
      )}
      {overlay === 'help' && (
        <AmbossOverlay style={{ top: topOffsetPx }}>
          <AmbossHelpOverlay />
        </AmbossOverlay>
      )}
      {overlay === 'history' && (
        <AmbossHistoryOverlay style={{ top: topOffsetPx }}>
          <AmbossHistoryView />
        </AmbossHistoryOverlay>
      )}
    </>
  );
}
